import { useEffect } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { ROUTES } from '@/utils/constants'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/** Параметр адреса, которым онбординг обходится: поддержка, тестовые аккаунты. */
const SKIP_ONBOARDING_PARAM = 'skip-onboarding'

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isImpersonating = useAuthStore((state) => state.isImpersonating)
  const user = useAuthStore((state) => state.user)
  const status = useOnboardingStore((state) => state.status)
  const ensureChecked = useOnboardingStore((state) => state.ensureChecked)
  const skipOnboarding = useOnboardingStore((state) => state.skip)
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const roles = user?.roles ?? []
  /**
   * Гейт — только для чистого ученика.
   *
   * Учитель со студенческой ролью (платформа их поддерживает: у dual-role
   * пользователей две сетки дашборда) иначе выбрасывался бы в мастер со всех
   * своих учительских страниц. Админ — тем более.
   *
   * Имперсонация исключена отдельно: под чужим токеном админ попал бы в
   * полноэкранный мастер, где нет ни баннера возврата, ни меню, а единственный
   * выход из него записал бы ученику отказ от онбординга — насовсем.
   */
  const gateApplies
    = roles.some((role) => role.slug === 'student')
    && !roles.some((role) => role.slug === 'teacher' || role.slug === 'admin')
    && !isImpersonating

  const skipRequested = searchParams.get(SKIP_ONBOARDING_PARAM) !== null

  useEffect(() => {
    if (!isAuthenticated || !gateApplies || user === null) {
      return
    }

    // Обход адресом снимает гейт только тому, кому он показан: иначе POST уходил
    // бы на каждый переход по сохранённой ссылке с этим параметром и заводил
    // профиль всякому, кто по ней прошёл.
    //
    // Отсюда два шага: сначала узнаём, нужен ли мастер вообще, и только потом
    // отказываемся от него. Спрашивать обязательно — при входе сразу по ссылке
    // с параметром ответа ещё нет, и проверка «нужен ли» без него отменяла бы
    // сам обход.
    if (skipRequested) {
      if (status === 'unknown') {
        void ensureChecked(user.id)
      } else if (status === 'required') {
        void skipOnboarding(user.id).catch(() => {
          // Отметка не сохранилась — гейт всё равно снят на эту сессию.
        })
      }

      return
    }

    void ensureChecked(user.id)
  }, [ensureChecked, gateApplies, isAuthenticated, skipOnboarding, skipRequested, status, user])

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  // Пока ответа про онбординг нет, страницу показываем: ждать его значило бы
  // подвешивать весь кабинет на один запрос профиля, а мастер — не то, ради
  // чего стоит держать человека перед пустым экраном.
  const onGateTarget
    = location.pathname === ROUTES.ONBOARDING
    || location.pathname.startsWith(`${ROUTES.ONBOARDING}/`)
    || location.pathname === ROUTES.PLACEMENT
    || location.pathname.startsWith(`${ROUTES.PLACEMENT}/`)

  // Сам мастер и обычная страница теста из-под гейта исключены: иначе редирект
  // уводил бы ученика с того самого экрана, куда он ведёт.
  if (gateApplies && status === 'required' && !skipRequested && !onGateTarget) {
    return <Navigate to={ROUTES.ONBOARDING} replace />
  }

  return <>{children}</>
}
